import { Badge } from 'base/ui/badge';
import { Button } from 'base/ui/button';
import { IconClear } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { Panel } from 'base/ui/panel';
import { TextRegular } from 'base/ui/typography';
import React from 'react';
import styles from './survey_form.css';

const FORM_CONFIGS: Record<string, FormConfig> = {
  v5: {
    storageKey: 'blocksSeenSurveyFormV5',
    showAfterMinutes: 5,
    typeformUrl: 'https://info786095.typeform.com/relume-survey',
  },
};

const config = FORM_CONFIGS.v5;
const showAfterMs = config.showAfterMinutes * 60 * 1000;

export function MaybeSurveyForm() {
  const [show, setShow] = React.useState(false);
  const visibleForMs = React.useRef(0);
  const visibleSince = React.useRef(Date.now());
  const timeout = React.useRef<number | null>(null);

  // Show after the tab has been visible for SHOW_AFTER_MS
  useTabVisibility({
    onBecomeVisible: React.useCallback(() => {
      timeout.current && clearTimeout(timeout.current);
      visibleSince.current = Date.now();
      if (show || localStorage[config.storageKey] === 'true') return;
      const remaining = Math.max(showAfterMs - visibleForMs.current, 0);
      timeout.current = setTimeout(() => setShow(true), remaining);
    }, [show]),
    onBecomeHidden: React.useCallback(() => {
      timeout.current && clearTimeout(timeout.current);
      const now = Date.now();
      visibleForMs.current += now - visibleSince.current;
    }, []),
  });

  return show ? (
    <div className={styles.surveyFloating}>
      <SurveyForm
        onClose={() => setShow(false)}
        config={config}
        isNew={hasSeenOldSurvey(config)}
      />
    </div>
  ) : null;
}

export function SurveyForm({
  onClose,
  config,
  isNew,
}: {
  onClose: () => void;
  config: FormConfig;
  isNew: boolean;
}) {
  useTabVisibility({
    onBecomeVisible: () => {
      localStorage[config.storageKey] = 'true';
    },
  });

  return (
    <Panel>
      <Rows spacing={16}>
        <Inline spacing={16}>
          <Rows spacing={8} xalign="start">
            {isNew && <Badge variant="ai" label="New survey" />}
            <TextRegular>
              We’d love to get your feedback on upcoming features
            </TextRegular>
          </Rows>
          <Button
            icon={IconClear}
            onClick={onClose}
            size="small"
            variant="subtle"
          />
        </Inline>
        <Inline spacing={16}>
          <Button
            variant="primary"
            label="Provide feedback"
            href={config.typeformUrl}
            target="_blank"
            onClick={onClose}
          />
        </Inline>
      </Rows>
    </Panel>
  );
}

function useTabVisibility(handlers: {
  onBecomeVisible?: () => void;
  onBecomeHidden?: () => void;
}) {
  const { onBecomeVisible, onBecomeHidden } = handlers;
  React.useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        onBecomeVisible?.();
      } else {
        onBecomeHidden?.();
      }
    };
    document.addEventListener('visibilitychange', handler);
    const cleanup = () =>
      document.removeEventListener('visibilitychange', handler);
    handler();
    return cleanup;
  }, [onBecomeVisible, onBecomeHidden]);
}

type FormConfig = {
  storageKey: string;
  showAfterMinutes: number;
  typeformUrl: string;
};

function hasSeenOldSurvey(currentConfig: FormConfig) {
  for (const config of Object.values(FORM_CONFIGS)) {
    if (config.storageKey === currentConfig.storageKey) continue;
    if (localStorage[config.storageKey] === 'true') return true;
  }
  return false;
}
